import { useQueries } from 'react-query';
import type { QueriesOptions, QueriesResults } from 'react-query';

export type AggregateQueryStatus = 'idle' | 'loading' | 'error' | 'success';

export interface AggregateQueriesResult<T extends unknown[]> {
  isLoading: boolean;
  data: boolean;
  isSuccess: boolean;
  status: AggregateQueryStatus;
  queries: QueriesResults<T>;
}

const getQueryStatus = (fetchState: {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}): AggregateQueryStatus => {
  const { isLoading, isError, isSuccess } = fetchState;

  if (isError) {
    return 'error';
  }

  if (isLoading) {
    return 'loading';
  }

  if (isSuccess) {
    return 'success';
  }

  return 'idle';
};

const useAggregateQueries = <T extends unknown[]>(
  queries: readonly [...QueriesOptions<T>],
): AggregateQueriesResult<T> => {
  const result = useQueries(queries);
  const states = result as unknown as {
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
  }[];
  const isLoading = states.some((query) => query.isLoading);
  const isError = states.some((query) => query.isError);
  const isSuccess = states.every((query) => query.isSuccess);
  const status = getQueryStatus({ isLoading, isError, isSuccess });

  return { isLoading, data: isError, isSuccess, status, queries: result };
};

export default useAggregateQueries;
